import { FC, useCallback, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  AccountTree as RulesIcon,
  Science as ScienceIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useToastr } from '@farmshare/ui-components';
import { useUserState } from 'providers/user-state-provider';
import { useCustomerOptions } from 'features/customers/hooks/use-customer-options';
import {
  EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
  EnumCutsheetAssignmentRuleSetRulesSplitType,
  EnumUserType,
  useCutsheetAssignmentRuleSetByCapabilityIdQuery,
  useCutsheetAssignmentRuleSetDeleteMutation,
  useCutsheetAssignmentRuleSetUpsertMutation,
  useCutsheetQuestionnaireManyQuery,
} from 'lib/graphql';

import {
  INSPECTION_LABELS,
  type LocalRule,
  RuleDialog,
  RuleTesterDialog,
  SPLIT_LABELS,
} from './assignment-rules';

type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

interface RuleGroupChip {
  label: string;
  color: ChipColor;
}

/**
 * Returns one chip per active filter dimension. Each chip's label is all
 * selected values joined by " | " and each dimension has a distinct color:
 *   inspectionLevel -> success (green)
 *   splitType -> secondary (purple)
 * @param rule
 * @param customerNameLookup
 */
function ruleGroupChips(
  rule: LocalRule,
  customerNameLookup?: (id: string) => string,
): RuleGroupChip[] {
  const groups: RuleGroupChip[] = [];
  if (rule.inspectionLevel.length > 0)
    groups.push({
      label: rule.inspectionLevel.map((v) => INSPECTION_LABELS[v]).join(' | '),
      color: 'success',
    });
  if (rule.splitType.length > 0) {
    const splitLabel = rule.splitType.map((v) => SPLIT_LABELS[v]).join(' | ');
    const indexSuffix =
      rule.splitIndex.length > 0
        ? ` (${rule.splitIndex.map((i) => `Q${i + 1}`).join(', ')})`
        : '';
    groups.push({ label: splitLabel + indexSuffix, color: 'secondary' });
  }
  if (rule.producer.length > 0) {
    const names = rule.producer
      .map((id) => customerNameLookup?.(id) ?? id)
      .join(' | ');
    const prefix = rule.producer.length === 1 ? 'Producer' : 'Producers';
    groups.push({ label: `${prefix}: ${names}`, color: 'info' });
  }
  if (rule.buyer.length > 0) {
    const names = rule.buyer
      .map((id) => customerNameLookup?.(id) ?? id)
      .join(' | ');
    const prefix = rule.buyer.length === 1 ? 'Buyer' : 'Buyers';
    groups.push({ label: `${prefix}: ${names}`, color: 'warning' });
  }
  return groups;
}

// ─── Unreachable rule detection ────────────────────────────────────────────────

/**
 * Returns true if dimension A fully covers dimension B.
 * "Covers" means: any order value that satisfies B also satisfies A.
 *   - A = [] (any)  covers  B = [] (any)  ✓
 *   - A = [] (any)  covers  B = [x, y]    ✓  (A matches everything B matches)
 *   - A = [x, y]   covers  B = [x]        ✓  (B's values are a subset of A's)
 *   - A = [x]      does NOT cover B = []  ✗  (B matches 'y', A does not)
 *   - A = [x]      does NOT cover B = [x, y] ✗ (B matches 'y', A does not)
 * @param aDim
 * @param bDim
 */
function dimensionCovered(aDim: string[], bDim: string[]): boolean {
  if (bDim.length === 0) return aDim.length === 0;
  return aDim.length === 0 || bDim.every((v) => aDim.includes(v));
}

/**
 * Returns true if rule A will always win before rule B (i.e. B is unreachable).
 * @param a
 * @param b
 */
function ruleCovers(a: LocalRule, b: LocalRule): boolean {
  // splitIndex: A covers B only if A has no constraint, or every index B specifies is also in A
  const splitIndexCovered =
    a.splitIndex.length === 0 ||
    (b.splitIndex.length === 0
      ? false
      : b.splitIndex.every((i) => a.splitIndex.includes(i)));
  return (
    dimensionCovered(a.inspectionLevel, b.inspectionLevel) &&
    dimensionCovered(a.splitType, b.splitType) &&
    splitIndexCovered &&
    dimensionCovered(a.producer, b.producer) &&
    dimensionCovered(a.buyer, b.buyer)
  );
}

/**
 * Returns a Set of uids for rules that are unreachable given their position.
 * @param rules
 */
function computeUnreachable(rules: LocalRule[]): Set<string> {
  const unreachable = new Set<string>();
  for (let i = 1; i < rules.length; i++) {
    for (let j = 0; j < i; j++) {
      if (!unreachable.has(rules[j].uid) && ruleCovers(rules[j], rules[i])) {
        unreachable.add(rules[i].uid);
        break;
      }
    }
  }
  return unreachable;
}

let _uidCounter = 0;
const nextUid = () => `rule-${++_uidCounter}-${Date.now()}`;

// ─── Sortable Rule Row ─────────────────────────────────────────────────────────

interface SortableRuleRowProps {
  rule: LocalRule;
  index: number;
  questionnaireName: string;
  isUnreachable: boolean;
  isDragOverlay?: boolean;
  customerNameLookup: (id: string) => string;
  onEdit: (uid: string) => void;
  onDelete: (uid: string) => void;
}

const SortableRuleRow: FC<SortableRuleRowProps> = ({
  rule,
  index,
  questionnaireName,
  isUnreachable,
  isDragOverlay = false,
  customerNameLookup,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.uid });

  const groupChips = ruleGroupChips(rule, customerNameLookup);
  const isCatchAll = groupChips.length === 0;

  return (
    <Card
      ref={setNodeRef}
      elevation={isDragOverlay ? 4 : 1}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragOverlay ? 'grabbing' : 'default',
        borderColor: isUnreachable ? 'warning.main' : undefined,
        borderWidth: isUnreachable ? 1 : undefined,
        borderStyle: isUnreachable ? 'solid' : undefined,
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Drag handle */}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              color: 'text.disabled',
              display: 'flex',
              alignItems: 'center',
              '&:hover': { color: 'text.secondary' },
              flexShrink: 0,
            }}
          >
            <DragIcon fontSize="small" />
          </Box>

          {/* Order badge */}
          <Typography
            variant="caption"
            sx={{
              minWidth: 24,
              color: 'text.secondary',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            #{index + 1}
          </Typography>

          {/* Rule content */}
          <Box flex={1} minWidth={0}>
            {rule.label && (
              <Typography variant="body2" fontWeight={600} noWrap>
                {rule.label}
              </Typography>
            )}
            <Box
              display="flex"
              gap={0.5}
              flexWrap="wrap"
              mt={rule.label ? 0.5 : 0}
            >
              {isCatchAll ? (
                <Chip
                  label="Catch-all (matches any order)"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ) : (
                groupChips.map((chip) => (
                  <Chip
                    key={chip.label}
                    label={chip.label}
                    size="small"
                    color={chip.color}
                    variant="outlined"
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Unreachable warning */}
          {isUnreachable && (
            <Tooltip title="This rule is unreachable — a previous rule already covers every order that would match here. Reorder or edit to fix.">
              <Chip
                icon={<WarningIcon />}
                label="Unreachable"
                size="small"
                color="warning"
                variant="filled"
              />
            </Tooltip>
          )}

          {/* Questionnaire badge */}
          <Chip
            label={questionnaireName || 'Unknown questionnaire'}
            size="small"
            color="primary"
            sx={{ flexShrink: 0, maxWidth: 200 }}
          />

          {/* Actions */}
          <Tooltip title="Edit rule">
            <IconButton size="small" onClick={() => onEdit(rule.uid)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove rule">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(rule.uid)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Main Tab Component ────────────────────────────────────────────────────────

interface AssignmentRulesTabProps {
  capabilityId: string;
}

export const AssignmentRulesTab: FC<AssignmentRulesTabProps> = ({
  capabilityId,
}) => {
  const { push } = useToastr();

  const { data: questionnaireData, loading: questionnairesLoading } =
    useCutsheetQuestionnaireManyQuery({ variables: { capabilityId } });

  const questionnaires = (
    questionnaireData?.cutsheetQuestionnairesByVendorAndAnimal ?? []
  )
    .filter(Boolean)
    .map((q) => ({ _id: q!._id as string, name: q!.name }));

  // ── Customer options for producer/buyer filters ─────────────────────────
  const { vendor } = useUserState();
  const vendorId = vendor?._id as string | undefined;
  const { customerOptions: producerOptions } = useCustomerOptions(
    vendorId,
    true,
    { relationshipType: EnumUserType.Producer },
  );
  const { customerOptions: buyerOptions } = useCustomerOptions(vendorId, true, {
    relationshipType: EnumUserType.Buyer,
  });

  const customerNameLookup = useCallback(
    (id: string) => {
      const producer = producerOptions.find((o) => o._id === id);
      if (producer) return producer.fullName;
      const buyer = buyerOptions.find((o) => o._id === id);
      if (buyer) return buyer.fullName;
      return id;
    },
    [producerOptions, buyerOptions],
  );

  // ── Remote data ──────────────────────────────────────────────────────────
  const { data, loading } = useCutsheetAssignmentRuleSetByCapabilityIdQuery({
    variables: { capabilityId },
    onCompleted: (d) => {
      const rs = d.cutsheetAssignmentRuleSetByCapabilityId;
      setDefaultQuestionnaireId(String(rs?.defaultQuestionnaireId ?? ''));
      setRules(
        (rs?.rules ?? []).filter(Boolean).map((r) => ({
          uid: nextUid(),
          label: r!.label ?? undefined,
          inspectionLevel: (r!.inspectionLevel ?? []).filter(
            Boolean,
          ) as EnumCutsheetAssignmentRuleSetRulesInspectionLevel[],
          splitType: (r!.splitType ?? []).filter(
            Boolean,
          ) as EnumCutsheetAssignmentRuleSetRulesSplitType[],
          splitIndex: (r!.splitIndex ?? []).filter(
            (v): v is number => v != null,
          ),
          producer: (r!.producer ?? [])
            .filter(Boolean)
            .map((p: unknown) => String(p)),
          buyer: (r!.buyer ?? [])
            .filter(Boolean)
            .map((b: unknown) => String(b)),
          questionnaireId: String(r!.questionnaireId),
        })),
      );
      setDirty(false);
    },
  });

  const [upsert, { loading: saving }] =
    useCutsheetAssignmentRuleSetUpsertMutation();
  const [deleteRuleSet, { loading: deleting }] =
    useCutsheetAssignmentRuleSetDeleteMutation();

  // ── Local state ──────────────────────────────────────────────────────────
  const [defaultQuestionnaireId, setDefaultQuestionnaireId] = useState('');
  const [rules, setRules] = useState<LocalRule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [testerOpen, setTesterOpen] = useState(false);

  // ── Unreachable detection ────────────────────────────────────────────────
  const unreachableUids = useMemo(() => computeUnreachable(rules), [rules]);

  // ── DnD sensors ─────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) =>
    setActiveId(String(event.active.id));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      setRules((prev) => {
        const oldIndex = prev.findIndex((r) => r.uid === active.id);
        const newIndex = prev.findIndex((r) => r.uid === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
      setDirty(true);
    }
  };

  // ── Rule CRUD ────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingUid(null);
    setDialogOpen(true);
  };

  const handleEdit = useCallback((uid: string) => {
    setEditingUid(uid);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback((uid: string) => {
    setRules((prev) => prev.filter((r) => r.uid !== uid));
    setDirty(true);
  }, []);

  const handleDialogSave = (ruleData: Omit<LocalRule, 'uid'>) => {
    if (editingUid) {
      setRules((prev) =>
        prev.map((r) =>
          r.uid === editingUid ? { ...ruleData, uid: r.uid } : r,
        ),
      );
    } else {
      setRules((prev) => [...prev, { ...ruleData, uid: nextUid() }]);
    }
    setDirty(true);
    setDialogOpen(false);
  };

  // ── Persist ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const input = rules.map((r, i) => ({
        sortOrder: i,
        label: r.label || undefined,
        inspectionLevel: r.inspectionLevel.length
          ? r.inspectionLevel
          : undefined,
        splitType: r.splitType.length ? r.splitType : undefined,
        splitIndex: r.splitIndex.length ? r.splitIndex : undefined,
        producer: r.producer.length ? r.producer : undefined,
        buyer: r.buyer.length ? r.buyer : undefined,
        questionnaireId: r.questionnaireId,
      }));
      await upsert({
        variables: {
          capabilityId,
          rules: input,
          defaultQuestionnaireId: defaultQuestionnaireId || undefined,
        },
      });
      setDirty(false);
      push({ title: 'Saved', body: 'Assignment rules updated', bg: 'success' });
    } catch {
      push({ title: 'Error', body: 'Failed to save rules', bg: 'error' });
    }
  };

  const handleClearAll = async () => {
    try {
      await deleteRuleSet({ variables: { capabilityId } });
      setRules([]);
      setDefaultQuestionnaireId('');
      setDirty(false);
      push({
        title: 'Cleared',
        body: 'All assignment rules removed',
        bg: 'success',
      });
    } catch {
      push({ title: 'Error', body: 'Failed to clear rules', bg: 'error' });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const activeRule = activeId ? rules.find((r) => r.uid === activeId) : null;
  const editingRule = editingUid
    ? rules.find((r) => r.uid === editingUid)
    : null;
  const questionnaireName = (id: string) =>
    questionnaires.find((q) => q._id === id)?.name ?? id;

  const hasUnreachable = unreachableUids.size > 0;

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading || questionnairesLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Assignment Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={640}>
            Rules are evaluated top-down. The first rule whose filters all match
            the incoming order wins. If no rule matches, the default form
            builder below is used.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ScienceIcon />}
            onClick={() => setTesterOpen(true)}
            disabled={questionnaires.length === 0}
          >
            Test Rules
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={questionnaires.length === 0}
          >
            Add Rule
          </Button>
        </Stack>
      </Box>

      {/* No questionnaires warning */}
      {questionnaires.length === 0 && (
        <Alert severity="warning">
          No form builders found for this capability. Create at least one before
          setting up assignment rules.
        </Alert>
      )}

      {/* Unreachable rules warning */}
      {hasUnreachable && (
        <Alert severity="warning" sx={{ pl: 3 }}>
          {unreachableUids.size === 1
            ? '1 rule is unreachable'
            : `${unreachableUids.size} rules are unreachable`}{' '}
          — a previous rule already covers every order those rules would match.
          Reorder or edit the flagged rules to fix.
        </Alert>
      )}

      {/* Default form */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Default Form
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Used when no rule above matches the incoming order. Leave blank to
            require manual form selection.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>Default form (optional)</InputLabel>
            <Select
              label="Default form (optional)"
              value={defaultQuestionnaireId}
              onChange={(e) => {
                setDefaultQuestionnaireId(e.target.value as string);
                setDirty(true);
              }}
            >
              <MenuItem value="">— None (manual selection) —</MenuItem>
              {questionnaires.map((q) => (
                <MenuItem key={q._id} value={q._id}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Divider />

      {/* Empty state */}
      {rules.length === 0 && questionnaires.length > 0 && (
        <Card elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <RulesIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              No Assignment Rules
            </Typography>
            <Typography variant="body2" color="text.secondary" maxWidth={400}>
              Add rules to automatically route orders to the right questionnaire
              based on inspection level, split type, or other criteria. Orders
              that don&apos;t match any rule fall back to the default above.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add First Rule
            </Button>
          </Stack>
        </Card>
      )}

      {/* Sortable list */}
      {rules.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rules.map((r) => r.uid)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={1}>
              {rules.map((rule, index) => (
                <SortableRuleRow
                  key={rule.uid}
                  rule={rule}
                  index={index}
                  questionnaireName={questionnaireName(rule.questionnaireId)}
                  isUnreachable={unreachableUids.has(rule.uid)}
                  customerNameLookup={customerNameLookup}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Stack>
          </SortableContext>

          <DragOverlay>
            {activeRule && (
              <SortableRuleRow
                rule={activeRule}
                index={rules.findIndex((r) => r.uid === activeRule.uid)}
                questionnaireName={questionnaireName(
                  activeRule.questionnaireId,
                )}
                isUnreachable={unreachableUids.has(activeRule.uid)}
                isDragOverlay
                customerNameLookup={customerNameLookup}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Save / Clear */}
      {(rules.length > 0 ||
        defaultQuestionnaireId ||
        data?.cutsheetAssignmentRuleSetByCapabilityId) && (
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Rule tester dialog */}
      <RuleTesterDialog
        open={testerOpen}
        rules={rules}
        questionnaires={questionnaires}
        defaultQuestionnaireId={defaultQuestionnaireId}
        vendorId={vendorId}
        onClose={() => setTesterOpen(false)}
      />

      {/* Add/Edit dialog */}
      <RuleDialog
        open={dialogOpen}
        initial={
          editingRule
            ? {
                label: editingRule.label,
                inspectionLevel: editingRule.inspectionLevel,
                splitType: editingRule.splitType,
                splitIndex: editingRule.splitIndex,
                producer: editingRule.producer,
                buyer: editingRule.buyer,
                questionnaireId: editingRule.questionnaireId,
              }
            : null
        }
        questionnaires={questionnaires}
        vendorId={vendorId}
        onSave={handleDialogSave}
        onClose={() => setDialogOpen(false)}
      />
    </Stack>
  );
};
